// Splits the flat HubSpot product list into the configurator's logical
// groupings. HubSpot has no category property; we derive categorization
// from the name. Display name + description overrides + "hide this product"
// rules layer in from rules.ts.

import type { Product } from "./api";
import { RULES, isTableId } from "./rules";

export type MachineFamily = "ascend" | "statler" | "vision";

export interface MachineProduct extends Product {
  category: "machine";
  family: MachineFamily;
  throatSize: number;
}

export interface UpgradeProduct extends Product {
  category: "upgrade";
}

export interface TableProduct extends Product {
  category: "table";
  tableType: "HP2" | "GS6";
  length: number; // feet
}

export interface AccessoryProduct extends Product {
  category: "accessory";
}

export type CategorizedProduct =
  | MachineProduct
  | UpgradeProduct
  | TableProduct
  | AccessoryProduct;

const FAMILIES: { match: string; key: MachineFamily }[] = [
  { match: "Ascend ", key: "ascend" },
  { match: "Statler ", key: "statler" },
  { match: "Vision ", key: "vision" },
];

const FAMILY_LABELS: Record<MachineFamily, string> = {
  ascend: "Ascend",
  statler: "Statler",
  vision: "Vision",
};

export function familyLabel(f: MachineFamily): string {
  return FAMILY_LABELS[f];
}

/** Parse "8' GS6 Table" / "12' HP2 Table" → {type, length}. */
function parseTable(name: string): { type: "HP2" | "GS6"; length: number } | null {
  const m = name.match(/^(\d+)'\s+(GS6|HP2)\s+Table$/i);
  if (!m) return null;
  return { type: m[2].toUpperCase() as "HP2" | "GS6", length: parseInt(m[1], 10) };
}

export function categorize(p: Product): CategorizedProduct {
  // Tables — check by name OR by ID (since our rules file enumerates them).
  if (isTableId(p.id)) {
    const parsed = parseTable(p.name);
    if (parsed) {
      return {
        ...p,
        category: "table",
        tableType: parsed.type,
        length: parsed.length,
      };
    }
  }
  // Upgrades — must be the full name "<Family>[ N] (Retrofit|Upgrade|Retro)"
  // to avoid catching accessory variants like "Zippered Leaders w/ Retrofit".
  if (/^(Ascend|Statler|Vision)(\s+\d+)?\s+(Retrofit|Upgrade|Retro)$/i.test(p.name)) {
    return { ...p, category: "upgrade" };
  }
  // Machines — Ascend/Statler/Vision + number.
  for (const { match, key } of FAMILIES) {
    if (p.name.startsWith(match)) {
      const size = parseInt(p.name.slice(match.length), 10);
      if (!isNaN(size)) {
        return { ...p, category: "machine", family: key, throatSize: size };
      }
    }
  }
  return { ...p, category: "accessory" };
}

export interface Catalog {
  machines: MachineProduct[];
  upgrades: UpgradeProduct[];
  tables: TableProduct[];
  accessories: AccessoryProduct[];
  byId: Map<string, CategorizedProduct>;
}

export function buildCatalog(products: Product[]): Catalog {
  // Drop hidden products. Original name + description preserved so image
  // lookups + matching against HubSpot stay stable; components call
  // `displayNameFor(p)` from format.ts when rendering customer-facing text.
  const visible = products.filter((p) => !RULES[p.id]?.hidden);
  const categorized = visible.map(categorize);
  return {
    machines: categorized
      .filter((p): p is MachineProduct => p.category === "machine")
      .sort((a, b) => a.throatSize - b.throatSize),
    upgrades: categorized.filter(
      (p): p is UpgradeProduct => p.category === "upgrade",
    ),
    tables: categorized
      .filter((p): p is TableProduct => p.category === "table")
      .sort((a, b) => a.length - b.length),
    accessories: categorized.filter(
      (p): p is AccessoryProduct => p.category === "accessory",
    ),
    byId: new Map(categorized.map((p) => [p.id, p])),
  };
}

export function machinesByFamily(
  catalog: Catalog,
  family: MachineFamily,
): MachineProduct[] {
  return catalog.machines.filter((m) => m.family === family);
}

/** The Statler/Ascend pair share throat sizes — find the same-size cousin
 *  in the other family. Used to flip a selection when the customer toggles
 *  "Upgrade to Statler Ascend". Returns null if no match. */
export function findFamilyEquivalent(
  catalog: Catalog,
  machine: MachineProduct,
  newFamily: MachineFamily,
): MachineProduct | null {
  return (
    catalog.machines.find(
      (m) => m.family === newFamily && m.throatSize === machine.throatSize,
    ) ?? null
  );
}
